import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper, Chip, Tooltip, CircularProgress, IconButton, Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ConfirmationDialog from 'routes/Pages/Woodchip/components/ConfirmationDialog';
import useContractDocs from '../CustomHooks/useContractDocs';

const ContractDocsSection = ({ contractUniqueID }) => {
  const { docs, uploading, fetchDocs, uploadDoc, downloadDoc, deleteDoc } = useContractDocs(contractUniqueID);

  const [pendingDeleteDoc, setPendingDeleteDoc] = useState(null);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    onDrop: acceptedFiles => {
      acceptedFiles.forEach(file => uploadDoc(file));
    },
  });

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteDoc) return;
    await deleteDoc(pendingDeleteDoc.id);
    setPendingDeleteDoc(null);
  };

  const getFileExtension = (name = '') => (name.split('.').pop() || '').toUpperCase();

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1, color: 'text.primary', letterSpacing: '.05em', fontSize: '0.75rem' }}>
        Documents
      </Typography>

      {/* Dropzone */}
      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          border: `1.5px dashed ${isDragActive ? '#1976d2' : '#ccc'}`,
          borderRadius: 2,
          backgroundColor: isDragActive ? '#e3f2fd' : 'transparent',
          p: 2.5,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color .15s, background .15s',
        }}>
        <input {...getInputProps()} />
        {uploading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 32, mb: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={500}>
              Browse files
            </Typography>
            <Typography variant="caption" color="text.secondary">
              or drag and drop here
            </Typography>
          </>
        )}
      </Paper>

      {/* File list */}
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {docs.length === 0 && !uploading && (
          <Typography variant="caption" color="text.secondary">
            No documents attached yet.
          </Typography>
        )}

        {docs.map(doc => {
          const ext = getFileExtension(doc.fileName || doc.docType || '');
          return (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                border: '0.5px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
              }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />

              <Tooltip title={doc.fileName || ''}>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.fileName || `Document ${doc.id}`}
                </Typography>
              </Tooltip>

              {ext && <Chip label={ext} size="small" sx={{ fontSize: '0.65rem', height: 20, px: 0.5 }} />}

              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => downloadDoc(doc.id, doc.documentName)}
                sx={{ fontSize: '0.72rem', py: 0.4, px: 1, minWidth: 0, textTransform: 'none' }}>
                Download
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => setPendingDeleteDoc(doc)}
                sx={{ fontSize: '0.72rem', py: 0.4, px: 1, minWidth: 0, textTransform: 'none' }}>
                Remove
              </Button>
            </Box>
          );
        })}
      </Box>

      {/* Delete confirmation */}
      <ConfirmationDialog
        open={!!pendingDeleteDoc}
        setOpen={v => {
          if (!v) setPendingDeleteDoc(null);
        }}
        confirmationText={`Remove "${pendingDeleteDoc?.fileName ||
          'this document'}" from the contract? This cannot be undone.`}
        confirmationFun={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ContractDocsSection;
